/**
 * Tracks "Added to Cart" events for Mixpanel
 * Captures when items are added to the cart with comprehensive product and cart data
 */

(function () {
  'use strict';

  // Store previous cart value to calculate "Cart Value Before"
  let previousCartValue = 0;
  let previousCartItems = [];
  let previousCartFullState = null; // Store full cart state for removal tracking
  let isInitialized = false;
  let isProcessingCartUpdate = false; // Prevent infinite loops
  let isProcessingMutation = false; // Prevent MutationObserver infinite loops

  /**
   * Gets the current cart state
   */
  async function getCartState() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Extracts product data from cart item
   */
  function extractProductDataFromCartItem(item) {
    return {
      product_id: item.product_id?.toString() || null,
      variant_id: item.variant_id?.toString() || null,
      sku: item.sku || null,
      price: item.price || 0,
      brand: item.vendor || item.product_vendor || null,
      product_title: item.product_title || item.title || null,
      quantity: item.quantity || 1,
    };
  }

  /**
   * Gets the most recently added or updated item from cart data
   * Compares with previous cart state to identify new items or quantity increases
   */
  async function getMostRecentItem(cartData) {
    // Get current cart state to compare
    const currentCart = await getCartState();
    if (!currentCart) {
      // If we can't get current cart, try to use cartData
      if (cartData && cartData.items && cartData.items.length > 0) {
        return cartData.items[cartData.items.length - 1];
      }
      return null;
    }

    // If current cart has items, find the newly added or updated one
    if (currentCart.items && currentCart.items.length > 0) {
      // Create a map of previous items by variant_id for quick lookup
      const previousItemsMap = new Map();
      previousCartItems.forEach((item) => {
        previousItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
      });

      // Find items that are new (not in previous cart) or have increased quantity
      const addedOrUpdatedItems = currentCart.items.filter((item) => {
        const variantId = item.variant_id?.toString();
        const previousQuantity = previousItemsMap.get(variantId) || 0;
        const currentQuantity = item.quantity || 0;

        // Item is new or quantity increased
        return currentQuantity > previousQuantity;
      });

      // Return the first added/updated item (or last item as fallback)
      // Note: previousCartItems is updated AFTER tracking, not here
      if (addedOrUpdatedItems.length > 0) {
        return addedOrUpdatedItems[0];
      }

      // If no new/updated items found but cart has items, return the last item as fallback
      return currentCart.items[currentCart.items.length - 1];
    }

    // If cart is empty, return null (shouldn't happen if item was just added, but handle gracefully)
    return null;
  }

  /**
   * Tracks the "Added to Cart" event
   */
  async function trackAddedToCart(event) {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      const cartData = event.detail?.cartData || event.cartData;
      if (!cartData) {
        return;
      }

      // Get the most recently added item
      const recentItem = await getMostRecentItem(cartData);
      if (!recentItem) {
        return;
      }

      // Extract product data
      const productData = extractProductDataFromCartItem(recentItem);

      // Get current cart state to get accurate total
      const currentCart = await getCartState();
      const cartValueAfter = currentCart?.total_price || cartData?.total_price || 0;

      // Use stored previous cart value for "before" (more accurate)
      // This is updated after each addition, so it represents the cart value before this addition
      const finalCartValueBefore = previousCartValue;

      // Track the event
      mixpanel.track('Added to Cart V.1', {
        'Product ID': productData.product_id || '',
        'Variant ID': productData.variant_id || '',
        SKU: productData.sku || '',
        Price: productData.price / 100, // Convert from cents to dollars
        Brand: productData.brand || '',
        'Product Title': productData.product_title || '',
        Quantity: productData.quantity || 1,
        'Cart Value Before': finalCartValueBefore / 100, // Convert from cents to dollars
        'Cart Value After': cartValueAfter / 100, // Convert from cents to dollars
        'User Agent': navigator.userAgent,
      });

      // Update previous cart value, items, and full state for next addition/removal
      previousCartValue = cartValueAfter;

      // Update previous cart full state and items
      const updatedCart = await getCartState();
      if (updatedCart) {
        previousCartFullState = updatedCart;
        previousCartItems = updatedCart.items.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
        }));
      }
    } catch (error) {
      // Error tracking Added to Cart
    }
  }

  /**
   * Checks if items were actually added to the cart (new items or quantity increases)
   * Compares current cart state with stored previous cart state
   */
  async function checkIfItemsAdded() {
    const currentCart = await getCartState();
    if (!currentCart || !currentCart.items) {
      return false;
    }

    // If previous cart was empty and current cart has items, items were definitely added
    if (previousCartItems.length === 0 && currentCart.items.length > 0) {
      return true;
    }

    // Create a map of previous items by variant_id
    const previousItemsMap = new Map();
    previousCartItems.forEach((item) => {
      previousItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
    });

    // Check if any items are new or have increased quantity
    const hasNewOrIncreasedItems = currentCart.items.some((item) => {
      const variantId = item.variant_id?.toString();
      const previousQuantity = previousItemsMap.get(variantId) || 0;
      const currentQuantity = item.quantity || 0;
      return currentQuantity > previousQuantity;
    });

    return hasNewOrIncreasedItems;
  }

  /**
   * Checks if items were actually removed from the cart
   * Compares current cart state with stored previous cart state
   */
  async function checkIfItemsRemoved() {
    const currentCart = await getCartState();

    // If previous cart had items and current cart is empty, items were definitely removed
    if (previousCartItems.length > 0 && (!currentCart || !currentCart.items || currentCart.items.length === 0)) {
      return true;
    }

    if (!currentCart || !currentCart.items) {
      return false;
    }

    // Create a map of current items by variant_id
    const currentItemsMap = new Map();
    currentCart.items.forEach((item) => {
      currentItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
    });

    // Check if any items were removed or have decreased quantity
    const hasRemovedOrDecreasedItems = previousCartItems.some((prevItem) => {
      const variantId = prevItem.variant_id?.toString();
      const previousQuantity = prevItem.quantity || 0;
      const currentQuantity = currentItemsMap.get(variantId) || 0;
      return currentQuantity < previousQuantity;
    });

    return hasRemovedOrDecreasedItems;
  }

  /**
   * Gets the most recently removed item from cart
   * Compares with previous cart state to identify the removed item
   */
  async function getMostRecentRemovedItem() {
    // Get current cart state to compare
    const currentCart = await getCartState();
    if (!currentCart) {
      return null;
    }

    // If we don't have previous full cart state, can't determine what was removed
    if (!previousCartFullState || !previousCartFullState.items) {
      return null;
    }

    // Create a map of current items by variant_id
    const currentItemsMap = new Map();
    if (currentCart.items) {
      currentCart.items.forEach((item) => {
        currentItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
      });
    }

    // Find items that were in previous cart but not in current, or have decreased quantity
    const removedItems = previousCartFullState.items.filter((prevItem) => {
      const variantId = prevItem.variant_id?.toString();
      const previousQuantity = prevItem.quantity || 0;
      const currentQuantity = currentItemsMap.get(variantId) || 0;
      return currentQuantity < previousQuantity;
    });

    // Return the first removed item with full data
    if (removedItems.length > 0) {
      // Calculate the quantity that was removed
      const removedItem = removedItems[0];
      const variantId = removedItem.variant_id?.toString();
      const previousQuantity = removedItem.quantity || 0;
      const currentQuantity = currentItemsMap.get(variantId) || 0;
      const quantityRemoved = previousQuantity - currentQuantity;

      // Return the item with the quantity that was actually removed
      return {
        ...removedItem,
        quantity: quantityRemoved, // The quantity that was removed, not the remaining quantity
      };
    }

    return null;
  }

  /**
   * Formats cart items for tracking
   */
  function formatCartItemsForTracking(items) {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    return items.map((item) => ({
      'Product Title': item.product_title || item.title || '',
      'Product ID': item.product_id?.toString() || '',
      'Variant ID': item.variant_id?.toString() || '',
      SKU: item.sku || '',
      Price: item.price ? item.price / 100 : 0, // Convert from cents to dollars
      Brand: item.vendor || item.product_vendor || '',
    }));
  }

  /**
   * Tracks the "Cart Drawer Viewed" event
   */
  async function trackCartDrawerViewed() {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      // Get current cart state
      const cart = await getCartState();
      if (!cart) {
        return;
      }

      // Extract cart data
      const itemCount = cart.item_count || 0;
      const cartValue = cart.total_price ? cart.total_price / 100 : 0; // Convert from cents to dollars
      const currency = cart.currency || 'USD';
      const items = formatCartItemsForTracking(cart.items);

      // Track the event
      mixpanel.track('Cart Drawer Viewed V.1', {
        'Item Count': itemCount,
        'Cart Value': cartValue,
        Currency: currency,
        Items: items,
        'User Agent': navigator.userAgent,
      });
    } catch (error) {
      // Error tracking Cart Drawer Viewed
    }
  }

  /**
   * Tracks the "Removed from Cart" event
   */
  async function trackRemovedFromCart(event) {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      const cartData = event.detail?.cartData || event.cartData;

      // Get the most recently removed item
      const removedItem = await getMostRecentRemovedItem();
      if (!removedItem) {
        return;
      }

      // Extract product data
      const productData = extractProductDataFromCartItem(removedItem);

      // Get current cart value (after removal)
      const currentCart = await getCartState();
      const cartValueAfter = currentCart?.total_price || cartData?.total_price || 0;

      // Use stored previous cart value for "before" (cart value before removal)
      const finalCartValueBefore = previousCartValue;

      // Track the event
      mixpanel.track('Removed from Cart V.1', {
        'Product ID': productData.product_id || '',
        'Variant ID': productData.variant_id || '',
        SKU: productData.sku || '',
        Price: productData.price / 100, // Convert from cents to dollars
        Brand: productData.brand || '',
        'Product Title': productData.product_title || '',
        Quantity: productData.quantity || 1,
        'Cart Value Before': finalCartValueBefore / 100, // Convert from cents to dollars
        'Cart Value After': cartValueAfter / 100, // Convert from cents to dollars
        'User Agent': navigator.userAgent,
      });

      // Update previous cart value, items, and full state for next removal
      previousCartValue = cartValueAfter;
      if (currentCart) {
        previousCartFullState = currentCart;
        previousCartItems = currentCart.items.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
        }));
      }
    } catch (error) {
      // Error tracking Removed from Cart
    }
  }

  /**
   * Tracks the "Begin Checkout" event
   */
  async function trackBeginCheckout() {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      // Get current cart state
      const cart = await getCartState();
      if (!cart) {
        return;
      }

      // Extract cart data (same as Cart Drawer Viewed)
      const itemCount = cart.item_count || 0;
      const cartValue = cart.total_price ? cart.total_price / 100 : 0; // Convert from cents to dollars
      const currency = cart.currency || 'USD';
      const items = formatCartItemsForTracking(cart.items);

      // Track the event
      mixpanel.track('Begin Checkout V.1', {
        'Item Count': itemCount,
        'Cart Value': cartValue,
        Currency: currency,
        Items: items,
        'User Agent': navigator.userAgent,
      });
    } catch (error) {
      // Error tracking Begin Checkout
    }
  }

  /**
   * Handle cart update events (shared by both PUB_SUB and DOM events)
   */
  async function handleCartUpdateEvent(event) {
    // Prevent infinite loops: if we're already processing a cart update, ignore subsequent updates
    if (isProcessingCartUpdate) {
      return;
    }

    isProcessingCartUpdate = true;
    try {
      // Check if items were actually added (quantity increases or new items)
      const itemsAdded = await checkIfItemsAdded();
      if (itemsAdded) {
        await trackAddedToCart(event);
      } else {
        // Check if items were removed (quantity decreases or items removed)
        const itemsRemoved = await checkIfItemsRemoved();
        if (itemsRemoved) {
          await trackRemovedFromCart(event);
        }
      }
    } finally {
      isProcessingCartUpdate = false;
    }
  }

  /**
   * Initialize cart tracking
   */
  function initCartTracking() {
    // Subscribe to cart update events - track all cart updates
    if (typeof subscribe !== 'undefined' && window.PUB_SUB_EVENTS) {
      const cartUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.cartUpdate, handleCartUpdateEvent);

      // Store unsubscriber for cleanup if needed
      if (cartUpdateUnsubscriber) {
        window.mixpanelCartTrackingUnsubscriber = cartUpdateUnsubscriber;
      }
    }

    // Also listen to DOM events as fallback (but only if PUB_SUB is not available)
    // Note: We keep both for backwards compatibility, but handleCartUpdateEvent prevents duplicate processing
    document.addEventListener('cart:updated', handleCartUpdateEvent);

    // Listen for cart drawer open events (multiple methods for reliability)

    // Method 1: Listen to the custom event if it's dispatched
    document.addEventListener('cart-drawer:open', () => {

      console.log('cart-drawer:open');
      trackCartDrawerViewed();
    });

    // Method 2: Watch for class changes on the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Use MutationObserver to watch for 'active' class being added
      const observer = new MutationObserver((mutations) => {
        // Prevent infinite loops from classList modifications
        if (isProcessingMutation) {
          return;
        }

        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('active') && !target.classList.contains('mixpanel-tracked-open')) {
              // Temporarily disconnect observer to prevent infinite loop
              observer.disconnect();
              isProcessingMutation = true;
              
              // Mark as tracked to avoid duplicate events
              target.classList.add('mixpanel-tracked-open');
              trackCartDrawerViewed();
              
              // Reconnect observer after a brief delay
              setTimeout(() => {
                observer.observe(cartDrawer, {
                  attributes: true,
                  attributeFilter: ['class'],
                });
                isProcessingMutation = false;
              }, 0);
            } else if (!target.classList.contains('active') && target.classList.contains('mixpanel-tracked-open')) {
              // Temporarily disconnect observer to prevent infinite loop
              observer.disconnect();
              isProcessingMutation = true;
              
              // Only remove if it's actually present to avoid unnecessary mutations
              target.classList.remove('mixpanel-tracked-open');
              
              // Reconnect observer after a brief delay
              setTimeout(() => {
                observer.observe(cartDrawer, {
                  attributes: true,
                  attributeFilter: ['class'],
                });
                isProcessingMutation = false;
              }, 0);
            }
          }
        });
      });

      // Start observing the cart drawer for class changes
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    } else {
      // If cart drawer doesn't exist yet, wait for it
      const checkForCartDrawer = setInterval(() => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          clearInterval(checkForCartDrawer);
          // Set up observer on the found drawer
          const observer = new MutationObserver((mutations) => {
            // Prevent infinite loops from classList modifications
            if (isProcessingMutation) {
              return;
            }

            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const target = mutation.target;
                if (target.classList.contains('active') && !target.classList.contains('mixpanel-tracked-open')) {
                  // Temporarily disconnect observer to prevent infinite loop
                  observer.disconnect();
                  isProcessingMutation = true;
                  
                  target.classList.add('mixpanel-tracked-open');
                  trackCartDrawerViewed();
                  
                  // Reconnect observer after a brief delay
                  setTimeout(() => {
                    observer.observe(drawer, {
                      attributes: true,
                      attributeFilter: ['class'],
                    });
                    isProcessingMutation = false;
                  }, 0);
                } else if (!target.classList.contains('active') && target.classList.contains('mixpanel-tracked-open')) {
                  // Temporarily disconnect observer to prevent infinite loop
                  observer.disconnect();
                  isProcessingMutation = true;
                  
                  // Only remove if it's actually present to avoid unnecessary mutations
                  target.classList.remove('mixpanel-tracked-open');
                  
                  // Reconnect observer after a brief delay
                  setTimeout(() => {
                    observer.observe(drawer, {
                      attributes: true,
                      attributeFilter: ['class'],
                    });
                    isProcessingMutation = false;
                  }, 0);
                }
              }
            });
          });
          observer.observe(drawer, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }, 500);

      // Stop checking after 10 seconds
      setTimeout(() => clearInterval(checkForCartDrawer), 10000);
    }

    // Listen for checkout button clicks
    // Use event delegation to catch all checkout buttons (cart drawer, main cart, etc.)
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const button = target.closest('button');

        // Check if the clicked element is a checkout button
        const isCheckoutButton =
          target.id === 'CartDrawer-Checkout' ||
          target.id === 'checkout' ||
          (button && button.id === 'CartDrawer-Checkout') ||
          (button && button.id === 'checkout') ||
          (button && button.name === 'checkout') ||
          (button && button.getAttribute('form') && button.name === 'checkout');

        if (isCheckoutButton) {
          trackBeginCheckout();
        }
      },
      true,
    ); // Use capture phase to catch early

    // Initialize previous cart value, items, and full state
    getCartState().then((cart) => {
      if (cart) {
        previousCartValue = cart.total_price || 0;
        previousCartItems = cart.items.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
        }));
        previousCartFullState = cart; // Store full cart state for removal tracking
        isInitialized = true;
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartTracking);
  } else {
    initCartTracking();
  }
})();
